import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Space, Button, Progress, Spin, Alert, Row, Col } from 'antd';
import { ArrowDownOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useGetApplicationByIdQuery } from '../../store/api/applicationsApi';
import { useGetRoutesExtendedQuery, type RouteObligationForPayment } from '../../store/api/financeApi';
import CreatePaymentModal from '../../components/finance/CreatePaymentModal';
import { MoneyCell } from '../../ui';
import { applicationRouteDirectionLabel } from '../../utils/addressUtils';
import { obligationTriggerCodeLabel } from '../../utils/enumUiLabels';
import { FinanceSourceDataHint } from '../../utils/financeDomainUi';
import { ObligationPaymentsBlock, type ObligationItem } from './FinanceRouteModal';

const { Text } = Typography;

type IncomeObligation = ObligationItem & { routeId: number };

export interface FinanceApplicationFinanceContentProps {
  visible: boolean;
  applicationId: number;
  onRefetch?: () => void | Promise<unknown>;
  /** Подсветка и прокрутка к обязательству в блоке «Получить от клиента» */
  focusObligationId?: number | null;
}

function mapApplicationToIncomeObligations(application: Record<string, unknown> | undefined): IncomeObligation[] {
  if (!application) return [];
  const rows = (application.financialObligations as unknown[]) ?? [];
  const out: IncomeObligation[] = [];
  for (const raw of rows) {
    const o = raw as Record<string, unknown>;
    if (String(o.direction || '').toUpperCase() !== 'INCOME') continue;
    if (String(o.counterpartyType || 'CLIENT').toUpperCase() !== 'CLIENT') continue;
    if (o.isAdjustment) continue;
    const id = o.id != null ? Number(o.id) : NaN;
    if (!Number.isFinite(id)) continue;

    const planned = Number(o.planned_amount ?? o.amountWithVAT ?? 0);
    let paid = Number(o.paid_amount ?? NaN);
    if (!Number.isFinite(paid)) {
      const pay = ((o.payments as unknown[]) || []).reduce<number>(
        (s, p) => s + Number((p as { amountPaid?: unknown }).amountPaid || 0),
        0
      );
      const alloc = ((o.paymentAllocations as unknown[]) || []).reduce<number>(
        (s, a) => s + Number((a as { amount?: unknown }).amount || 0),
        0
      );
      const applied = Number(o.appliedAmount ?? 0);
      paid = Math.round((pay + alloc + applied) * 100) / 100;
    }
    let rem = Number(o.remaining_amount ?? NaN);
    if (!Number.isFinite(rem)) {
      rem = Math.max(0, Math.round((planned - paid) * 100) / 100);
    }

    out.push({
      id,
      direction: 'INCOME',
      dueDate: (o.dueDate ?? o.due_date) as string | null | undefined,
      remainingAmount: rem,
      amountWithVAT: planned,
      totalPaid: paid,
      stage: o.stage as string,
      triggerCode: o.triggerCode as string | undefined,
      triggerDate: (o.baseDate as string | null) ?? null,
      delayDays:
        o.delay_days != null && Number.isFinite(Number(o.delay_days))
          ? Math.max(0, Math.floor(Number(o.delay_days)))
          : (o.delayDays != null && Number.isFinite(Number(o.delayDays))
              ? Math.max(0, Math.floor(Number(o.delayDays)))
              : 0),
      isActive: o.is_active !== false,
      routeId: 0,
    });
  }
  return out;
}

function buildAppPaymentRowsForModal(application: Record<string, unknown> | undefined): RouteObligationForPayment[] {
  const items = mapApplicationToIncomeObligations(application);
  return items
    .filter((o) => (o.remainingAmount ?? 0) > 0.005 && o.isActive !== false)
    .map((o) => ({
      id: o.id!,
      direction: 'INCOME',
      stage: String(o.stage || ''),
      amountWithVAT: Number(o.amountWithVAT ?? 0),
      remainingAmount: Number(o.remainingAmount ?? 0),
      dueDate: o.dueDate ?? null,
      routeId: null,
      applicationId: application?.id != null ? Number(application.id) : null,
    }));
}

export const FinanceApplicationFinanceContent: React.FC<FinanceApplicationFinanceContentProps> = ({
  visible,
  applicationId,
  onRefetch,
  focusObligationId = null,
}) => {
  const [createPaymentModalVisible, setCreatePaymentModalVisible] = useState(false);
  const [initialObligationId, setInitialObligationId] = useState<number | null>(null);
  const [payModalRouteId, setPayModalRouteId] = useState<number | null>(null);
  const [highlightObligationId, setHighlightObligationId] = useState<number | null>(null);

  const { data: application, isLoading: loadingApp, isError: appError, refetch: refetchApp } =
    useGetApplicationByIdQuery(applicationId, {
      skip: !visible || !applicationId,
    });

  const {
    data: routesExtendedData,
    isLoading: loadingRoutes,
    refetch: refetchRoutesExt,
  } = useGetRoutesExtendedQuery(
    { page: 1, limit: 100, applicationId },
    { skip: !visible || !applicationId }
  );

  const fromApp = useMemo(
    () => mapApplicationToIncomeObligations(application as Record<string, unknown> | undefined),
    [application]
  );

  const recvObl = fromApp;

  const appPaymentRowsForModal = useMemo(
    () => buildAppPaymentRowsForModal(application as Record<string, unknown> | undefined),
    [application]
  );

  const nearestDue = useMemo(() => {
    const dues: string[] = [];
    for (const o of recvObl) {
      if ((o.remainingAmount ?? 0) > 0.005 && o.dueDate) {
        dues.push(String(o.dueDate));
      }
    }
    let nearest: string | null = null;
    for (const raw of dues) {
      const t = new Date(raw).getTime();
      if (Number.isNaN(t)) continue;
      if (nearest == null || t < new Date(nearest).getTime()) nearest = raw;
    }
    return nearest;
  }, [recvObl]);

  const prepRecvList = recvObl.filter((o) => o.stage === 'PREPAYMENT');
  const finalRecvList = recvObl.filter((o) => o.stage === 'FINAL');

  const canAddIncomePayment = recvObl.some(
    (o) => o.isActive !== false && (o.remainingAmount ?? 0) > 0.005
  );

  useEffect(() => {
    if (!visible || focusObligationId == null || !Number.isFinite(focusObligationId)) {
      setHighlightObligationId(null);
      return;
    }
    const el = document.getElementById(`app-finance-obl-${focusObligationId}`);
    if (!el) {
      return;
    }
    setHighlightObligationId(focusObligationId);
    const t1 = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 280);
    const t2 = window.setTimeout(() => setHighlightObligationId(null), 2600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [visible, focusObligationId, applicationId, recvObl]);

  const oblTotalAmount = recvObl.reduce((s, o) => s + Number(o.amountWithVAT ?? 0), 0);
  const oblTotalPaid = recvObl.reduce((s, o) => s + Number(o.totalPaid ?? 0), 0);
  const oblRemaining = recvObl.reduce((s, o) => s + Number(o.remainingAmount ?? 0), 0);

  const displayRequired = oblTotalAmount;
  const displayPaid = oblTotalPaid;
  const displayRemaining = oblRemaining;

  const directionLabel = application
    ? applicationRouteDirectionLabel({
        loadingCity: (application as { loadingCity?: string }).loadingCity,
        unloadingCity: (application as { unloadingCity?: string }).unloadingCity,
        loadingAddress: application.loadingAddress,
        unloadingAddress: application.unloadingAddress,
      })
    : '—';

  const openIncomePayment = (routeId: number | null | undefined, obligationId: number | null) => {
    const rid = routeId != null && routeId > 0 ? routeId : null;
    setPayModalRouteId(rid);
    setInitialObligationId(obligationId);
    setCreatePaymentModalVisible(true);
  };

  const afterPaymentSuccess = async () => {
    setCreatePaymentModalVisible(false);
    setInitialObligationId(null);
    setPayModalRouteId(null);
    await onRefetch?.();
    await refetchRoutesExt();
    await refetchApp();
  };

  if (appError) {
    return <Alert type="error" showIcon message="Не удалось загрузить заявку" />;
  }

  if (!applicationId) return null;

  return (
    <>
      {loadingApp && !application ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : null}

      {application ? (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Text type="secondary">Направление: </Text>
              <Text>{directionLabel}</Text>
            </Col>
            <Col span={24} style={{ marginTop: 8 }}>
              <Text type="secondary">Заявка: </Text>
              <Text>
                {application.internalNumber}
                {application.applicationDate &&
                  ` от ${dayjs(application.applicationDate as string).format('DD.MM.YYYY')}`}
              </Text>
            </Col>
          </Row>

          {routesExtendedData?.routes?.length === 0 && !loadingRoutes && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Нет маршрутов по заявке — доход по заявке показан по договору; после привязки маршрутов добавится детализация по ним."
            />
          )}

          <Card
            size="small"
            title={
              <Space>
                <ArrowDownOutlined style={{ color: '#52c41a' }} />
                <span>Получить от клиента</span>
              </Space>
            }
            extra={
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                disabled={!canAddIncomePayment}
                onClick={() => {
                  const target =
                    recvObl.find(
                      (o) => o.isActive !== false && (o.remainingAmount ?? 0) > 0.005
                    ) ?? null;
                  if (!target?.id) return;
                  openIncomePayment(null, target.id);
                }}
              >
                Добавить платеж
              </Button>
            }
            style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}
          >
            <FinanceSourceDataHint domain="application" />
            {loadingApp && recvObl.length === 0 ? (
              <Text type="secondary">Загрузка финансов заявки…</Text>
            ) : recvObl.length > 0 || displayRequired > 0.005 ? (
              <>
                <div style={{ fontSize: 12, marginBottom: 8 }}>
                  {prepRecvList.map((o, i) => (
                    <div key={`pre-${o.id ?? i}`}>
                      Предоплата: {obligationTriggerCodeLabel(o.triggerCode)}, Отсрочка {o.delayDays ?? '—'} дн.
                    </div>
                  ))}
                  {finalRecvList.map((o, i) => (
                    <div key={`fin-${o.id ?? i}`}>
                      Доплата: {obligationTriggerCodeLabel(o.triggerCode)}, Отсрочка {o.delayDays ?? '—'} дн.
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    Срок оплаты (ближайший по открытым обязательствам):{' '}
                    {nearestDue ? dayjs(nearestDue).format('DD.MM.YYYY') : '—'}
                  </Text>
                </div>
                {displayRequired > 0.005 && (
                  <>
                    <Progress
                      percent={Math.min(
                        100,
                        Math.round((displayPaid / displayRequired) * 100)
                      )}
                      size="small"
                      showInfo={false}
                      strokeColor={displayRemaining > 0.005 ? '#faad14' : '#52c41a'}
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      <MoneyCell value={displayPaid} type="income" /> /{' '}
                      <MoneyCell value={displayRequired} type="income" />
                    </Text>
                    {displayRemaining > 0.005 && (
                      <div style={{ marginTop: 4 }}>
                        <Text strong>
                          Остаток к получению: <MoneyCell value={displayRemaining} type="income" />
                        </Text>
                      </div>
                    )}
                  </>
                )}
                {recvObl
                  .filter((o) => o.id)
                  .map((obl) => (
                    <div
                      key={obl.id}
                      id={obl.id ? `app-finance-obl-${obl.id}` : undefined}
                      style={
                        highlightObligationId != null && obl.id === highlightObligationId
                          ? {
                              outline: '2px solid #1890ff',
                              borderRadius: 8,
                              padding: 4,
                              marginBottom: 4,
                            }
                          : { marginBottom: 4 }
                      }
                    >
                      <ObligationPaymentsBlock
                        obligation={obl}
                        visible={visible}
                        moneyType="income"
                        onAddPayment={() => openIncomePayment(null, obl.id ?? null)}
                      />
                    </div>
                  ))}
                {displayRequired <= 0.005 && recvObl.filter((o) => o.id).length === 0 && (
                  <Text type="secondary">Нет данных по доходу</Text>
                )}
              </>
            ) : (
              <Text type="secondary">Нет данных</Text>
            )}
          </Card>
        </>
      ) : null}

      <CreatePaymentModal
        visible={createPaymentModalVisible}
        routeId={payModalRouteId}
        routeApplicationId={applicationId}
        initialObligationId={initialObligationId}
        preloadedObligations={
          payModalRouteId == null && appPaymentRowsForModal.length > 0 ? appPaymentRowsForModal : null
        }
        obligationDirectionFilter="INCOME"
        onClose={() => {
          setCreatePaymentModalVisible(false);
          setInitialObligationId(null);
          setPayModalRouteId(null);
        }}
        onSuccess={afterPaymentSuccess}
      />
    </>
  );
};

export default FinanceApplicationFinanceContent;
